<template>
  <v-layout row wrap>
    <v-flex xs12 class="title mb-4 capitalize"
      >{{ action }} ({{ bot.botTitle }}) configuration
    </v-flex>
    <v-flex xs12 sm6>
      <label class="d-block text-bold">
        Bot
        <v-select
          class="pt-0"
          v-model="input.botID"
          :items="bots"
          item-text="text"
          item-value="value"
          :disabled="true"
          required
        />
      </label>
    </v-flex>
    <v-flex xs12 sm6>
      <label class="d-block text-bold">
        Active Model
        <v-select
          class="pt-0"
          v-model="input.activeModel"
          :items="
            llmModels.map((item) => ({ text: item || 'Disable', value: item }))
          "
          item-text="text"
          item-value="value"
          placeholder="Active Model"
          @change="generateModelForm"
        />
      </label>
    </v-flex>
    <v-flex xs12>
      <div v-if="input.activeModel && input[input.activeModel]">
        <label class="d-block text-bold">
          API Key
          <v-text-field
            class="pt-0"
            v-model="input[input.activeModel].APIkey"
            placeholder="API Key"
            required
          />
        </label>

        <label class="d-block text-bold">
          Instruction Prompt
          <v-tooltip top>
            <template v-slot:activator="{ on, attrs }">
              <v-icon v-bind="attrs" v-on="on" size="16"
                >mdi-help-circle</v-icon
              >
            </template>
            <span>
              Instruction Prompt is the prompt that will be used to define the
              behavior, role & tasks for the bot. examples
            </span>
          </v-tooltip>
          <div style="font-size: 12px; font-weight: 300">
            More Examples
            <a href="https://platform.openai.com/examples" target="_">here</a>
            and
            <a
              href="https://helpwise.io/blog/customer-service-chatgpt-prompts"
              target="_"
              >here</a
            >
          </div>
          <v-textarea
            class="pt-0"
            v-model="input[input.activeModel].instructionPrompt"
            placeholder="Instruction Prompt"
            required
          />
        </label>

        <div class="tools">
          <label class="title mb-3">Tools</label>
          <template v-if="input[input.activeModel].tools">
            <div
              class="tool-item"
              style="position: relative"
              v-for="(item, key) in input[input.activeModel].tools"
              :key="key"
            >
              <v-flex
                style="height: 20px; position: absolute; right: 0px; top: 6px"
              >
                <v-btn
                  small
                  color="#0d6cb017"
                  center
                  style="height: 20px; padding: 0 10px"
                  @click="previewTool(key)"
                >
                  <template v-if="preview[key]">
                    <v-icon small color="blue">arrow_left</v-icon> Back to The
                    Form
                  </template>
                  <template v-else>
                    Preview & Test
                    <v-icon small class="ml-1" color="blue">send</v-icon>
                  </template>
                </v-btn>

                <v-btn
                  small
                  color="#0d6cb017"
                  style="height: 20px; padding: 0"
                  @click="copyTool(key)"
                >
                  <template v-if="copiedState[key]">
                    <v-icon small color="green">check</v-icon> Copied
                  </template>
                  <template v-else>
                    <v-icon small color="blue">content_copy</v-icon> Copy
                  </template>
                </v-btn>

                <v-btn
                  @click="removeTool(key)"
                  small
                  color="#0d6cb017"
                  style="height: 20px; padding: 0"
                  ><v-icon small color="red">delete_outline</v-icon>
                  Remove</v-btn
                >
              </v-flex>
              <template v-if="preview[key]">
                <div class="tools my-4">
                  <label class="text-bold">Preview</label>
                  <v-layout
                    row
                    wrap
                    justify-space-between
                    align-center
                    class="tools pa-0"
                  >
                    <v-flex
                      grow
                      style="
                        overflow-y: auto;
                        max-width: calc(100% - 160px);
                        white-space: nowrap;
                      "
                    >
                      <span class="blue--text mr-1">{{
                        preview[key].api.method
                      }}</span>
                      <span>{{ preview[key].api.endpoint }}</span>
                    </v-flex>
                    <v-flex style="max-width: 160px; text-align: end">
                      <v-btn
                        small
                        class="text-bold"
                        color="#0d6cb017"
                        @click="testAPIRequest(key)"
                        >Send Request</v-btn
                      >
                    </v-flex>
                  </v-layout>

                  <div>
                    <label>Headers:</label>
                    <div
                      class="px-4"
                      v-for="(header, headerKey) in preview[key].api.headers"
                      :key="headerKey"
                    >
                      <span>{{ headerKey }}: {{ header }}</span>
                    </div>
                  </div>
                  <div>
                    <label>
                      {{
                        preview[key].api.method === "GET"
                          ? "Query Params"
                          : "Body"
                      }}
                      :</label
                    >
                    <div
                      class="px-4"
                      v-for="(body, bodyKey) in preview[key].api.body"
                      :key="bodyKey"
                    >
                      <span v-if="!preview[key].schema[bodyKey]"
                        >{{ bodyKey }}: {{ body }}</span
                      >
                      <v-layout row align-center v-else>
                        <v-flex md3>
                          <span>{{ bodyKey }}:</span></v-flex
                        >
                        <v-flex md9>
                          <div v-if="preview[key].schema[bodyKey] === 'number'">
                            <v-text-field
                              hide-details
                              class="mt-0 pt-0"
                              v-model="preview[key].api.body[bodyKey]"
                              type="number"
                              @change="updateTestURL(key)"
                            />
                          </div>
                          <div
                            v-else-if="
                              preview[key].schema[bodyKey] === 'boolean'
                            "
                          >
                            <v-select
                              class="mt-0 pt-0"
                              hide-details
                              v-model="preview[key].api.body[bodyKey]"
                              :items="[true, false]"
                              @change="updateTestURL(key)"
                            />
                          </div>
                          <div v-else>
                            <v-text-field
                              class="mt-0 pt-0"
                              hide-details
                              v-model="preview[key].api.body[bodyKey]"
                              @change="updateTestURL(key)"
                            />
                          </div>
                        </v-flex>
                      </v-layout>
                    </div>
                  </div>
                </div>

                <div class="tools" v-if="previewResponse[key]">
                  <label class="text-bold">Response</label>
                  <pre style="text-wrap: pretty">{{
                    previewResponse[key]
                  }}</pre>
                </div>
              </template>
              <template v-else>
                <label class="d-block text-bold pt-2">
                  Name
                  <v-text-field
                    class="pt-0"
                    v-model="item.name"
                    placeholder="Name"
                  />
                </label>
                <label class="d-block text-bold px-2">
                  Description
                  <v-textarea
                    class="pt-0"
                    v-model="item.description"
                    placeholder="Description"
                  />
                </label>

                <v-layout row wrap>
                  <v-flex md3>
                    <label class="d-block text-bold">
                      Request Method
                      <v-select
                        class="pt-0"
                        :items="['GET', 'POST', 'PUT', 'PATCH']"
                        v-model="item.api.method"
                      />
                    </label>
                  </v-flex>
                  <v-flex md9>
                    <label class="d-block text-bold">
                      URL
                      <v-text-field
                        class="pt-0"
                        v-model="item.api.endpoint"
                        placeholder="endpoint"
                      />
                    </label>
                  </v-flex>
                  <v-flex xs12>
                    Headers
                    <v-btn fab small color="#0d6cb017" @click="addHeader(key)"
                      ><v-icon small color="blue">add</v-icon></v-btn
                    >
                    <v-layout
                      class="tool-item"
                      md12
                      row
                      v-for="(header, headerKey) in item.api.headers"
                      :key="headerKey"
                    >
                      <v-flex md3>
                        <label class="d-block text-bold">
                          Key
                          <v-text-field
                            class="pt-0"
                            v-model="item.api.headers[headerKey].key"
                            placeholder="key"
                          />
                        </label>
                      </v-flex>
                      <v-flex md9>
                        <label class="d-block text-bold">
                          Value
                          <v-text-field
                            class="pt-0"
                            v-model="item.api.headers[headerKey].value"
                            placeholder="value"
                          />
                        </label>
                      </v-flex>
                      <v-btn
                        @click="removeHeader(key, headerKey)"
                        fab
                        small
                        color="#0d6cb017"
                        class="delete-button mt-3"
                        ><v-icon small color="red"
                          >delete_outline</v-icon
                        ></v-btn
                      >
                    </v-layout>
                  </v-flex>
                  <v-flex xs12>
                    <label class="text-bold black--text"
                      >Parameters Schema</label
                    >
                    <v-btn fab small color="#0d6cb017" @click="addParam(key)"
                      ><v-icon small color="blue">add</v-icon></v-btn
                    >
                    <v-layout
                      class="tool-item"
                      md12
                      row
                      v-for="(param, paramKey) in item.schema"
                      :key="paramKey"
                    >
                      <v-flex md3>
                        <label class="d-block text-bold">
                          Key
                          <v-text-field
                            class="pt-0"
                            v-model="item.schema[paramKey].key"
                            placeholder="key"
                          />
                        </label>
                      </v-flex>
                      <v-flex md9>
                        <label class="d-block text-bold">
                          Type
                          <v-select
                            class="pt-0"
                            :items="schemaOptions"
                            v-model="item.schema[paramKey].type"
                          />
                        </label>
                      </v-flex>
                      <v-btn
                        @click="removeParam(key, paramKey)"
                        fab
                        small
                        color="#0d6cb017"
                        class="delete-button mt-3"
                        ><v-icon small color="red"
                          >delete_outline</v-icon
                        ></v-btn
                      >
                    </v-layout>
                  </v-flex>
                  <v-flex v-if="item.api.method !== 'GET'" xs12>
                    <label class="text-bold black--text">Body</label> (Optional)
                    <v-btn fab small color="#0d6cb017" @click="addBody(key)"
                      ><v-icon small color="blue">add</v-icon></v-btn
                    >
                    <v-layout
                      class="tool-item"
                      md12
                      row
                      v-for="(body, bodyKey) in item.api.body"
                      :key="bodyKey"
                    >
                      <v-flex md3>
                        <label class="d-block text-bold">
                          Key
                          <v-text-field
                            class="pt-0"
                            v-model="item.api.body[bodyKey].key"
                            placeholder="key"
                          />
                        </label>
                      </v-flex>
                      <v-flex md9>
                        <label class="d-block text-bold">
                          Value
                          <v-text-field
                            class="pt-0"
                            v-model="item.api.body[bodyKey].value"
                            placeholder="value"
                          />
                        </label>
                      </v-flex>
                      <v-btn
                        @click="removeBody(key, bodyKey)"
                        fab
                        small
                        color="#0d6cb017"
                        class="delete-button mt-3"
                        ><v-icon small color="red"
                          >delete_outline</v-icon
                        ></v-btn
                      >
                    </v-layout>
                  </v-flex>
                </v-layout>
              </template>
            </div>
          </template>
          <div>
            <v-btn small @click="addTool">
              <v-icon small color="blue">add</v-icon> Add Tool
            </v-btn>
            <v-btn
              v-if="Object.keys(copy).length"
              small
              color="#0d6cb017"
              @click="pasteTool"
            >
              <v-icon small color="blue">content_paste</v-icon> Paste
            </v-btn>
          </div>
        </div>
      </div>
    </v-flex>
    <v-flex xs12>
      <v-btn class="mt-4" @click="submit" color="primary" dark>
        {{ action === "add" ? "Add" : "Update" }}
      </v-btn>
    </v-flex>
  </v-layout>
</template>

<script>
export default {
  name: "LLMForm",
  props: {
    data: {
      type: Object,
      default: null,
    },
    currentBots: {
      type: Array,
      default: () => [],
    },
    action: {
      type: String,
      default: "add",
    },
  },
  data() {
    return {
      isValid: false,
      putClone: null,
      envToken: "",
      schemaOptions: ["string", "number", "boolean"].map((v) => ({
        text: v,
        value: v,
      })),
      input: {
        botID: "",
        activeModel: "",
      },
      accountID: "",
      copy: {},
      copiedState: {},
      preview: {},
      previewResponse: {},
    };
  },
  async mounted() {
    this.accountID = this.$store.state.user.userData.corporate_id;
    await this.$store.dispatch("llm/models");
    this.input = this.handleInput(this.$_.cloneDeep(this.data));
  },
  watch: {
    data(val) {
      if (val) {
        this.input = this.handleInput(val);
       // console.log('this.input', this.input)
        // this.putClone = this.$_.cloneDeep(val);
      } else this.resetForm();
    },
    llmModels(val) {
      if(val) {
        this.input = this.handleInput(this.data);
      }
    }
  },
  computed: {
    bot() {
      return this.$store.state.bot;
    },
    bots() {
      return this.$store.getters["bot/all"]
        .map((item) => ({ text: item.title, value: item.id }))
        .filter((i) =>
          this.action === "edit" && i.value === this.data.botID
            ? true
            : !this.currentBots.includes(i.value)
        );
    },
    llmModels() {
      return ["", ...this.$store.getters["llm/models"]];
    },
  },
  methods: {
    handleKey(key) {
      // Remove special characters and spaces, keep alphanumeric and hyphen/underscore
      return key.replace(/[^a-zA-Z0-9-_]/g, '');
    },
    handleInput(input) {
      input = JSON.parse(JSON.stringify(input))
      this.llmModels.forEach((model) => {
        if (input[model]) {
          input[model].tools.forEach((tool) => {
            // convert tool.api.headers object to array has key and value
            if ("api" in tool && "headers" in tool.api) {
              tool.api.headers = Object.entries(tool.api.headers).map(
                ([key, value]) => ({ key: this.handleKey(key), value })
              );
            }
            // convert tool.api.body object to array has key and value
            if ("api" in tool && "body" in tool.api) {
              tool.api.body = Object.entries(tool.api.body).map(
                ([key, value]) => ({ key: this.handleKey(key), value })
              );
            }
            // convert tool.schema object to array has key and value
            if ("schema" in tool) {
              tool.schema = Object.entries(tool.schema).map(([key, value]) => ({
                key: this.handleKey(key),
                type: value,
              }));
            }
          });
        }
      });
      return input;
    },
    handleOutput(input) {
      let output = JSON.parse(JSON.stringify(input));
      this.llmModels.forEach((model) => {
        if (output[model] && output[model].tools) {
          output[model].tools.forEach((tool) => {
            this.handleTool(tool);
          });
        }
      });
      return output;
    },
    handleTool(tool) {
      if ("api" in tool && "headers" in tool.api) {
        tool.api.headers = Object.fromEntries(
          tool.api.headers.map((value) => [this.handleKey(value.key), value.value])
        );
      }

      if ("api" in tool && tool.api.body) {
        tool.api.body = Object.fromEntries(
          tool.api.body.map((value) => [this.handleKey(value.key), value.value])
        );
      }

      if ("schema" in tool) {
        tool.schema = Object.fromEntries(
          tool.schema.map((value) => [this.handleKey(value.key), value.type])
        );
      }
      return tool;
    },
    generateModelForm() {
      if (this.input.activeModel) {
        if (!this.input[this.input.activeModel]) {
          this.$set(this.input, this.input.activeModel, {
            APIkey: "",
            instructionPrompt: "",
            tools: [],
          });
        }
      }
    },
    addTool() {
      // if (this.input[this.input.activeModel]) {
      this.input[this.input.activeModel].tools.push({
        name: "",
        description: "",
        schema: [{ key: "input", type: "string" }],
        toolType: 1,
        api: {
          endpoint: "",
          method: "POST",
          headers: [{ key: "Content-Type", value: "application/json" }],
          body: [{ key: "", value: "" }],
        },
      });
      // }
    },
    removeTool(index) {
      this.input[this.input.activeModel].tools.splice(index, 1);
    },
    previewTool(index) {
      if (this.preview[index]) {
        this.$set(this.preview, index, null);
      } else {
        const tool = this.handleTool(
          JSON.parse(
            JSON.stringify(this.input[this.input.activeModel].tools[index])
          )
        );
        for (const schema in tool.schema) {
          if (Object.hasOwnProperty.call(tool.schema, schema)) {
            tool.api.body[schema] = "";
          }
        }
        this.$set(this.preview, index, tool);
      }
    },
    updateTestURL(index) {
      const api = this.preview[index].api;
      if (api.method === "GET") {
        const url = new URL(this.preview[index].api.endpoint);
        for (const key in this.preview[index].api.body) {
          if (Object.hasOwnProperty.call(this.preview[index].api.body, key)) {
            url.searchParams.set(key, this.preview[index].api.body[key]);
          }
        }
        api.endpoint = url.toString();
        this.$set(this.preview[index], "api", api);
      }
    },
    addHeader(index) {
      this.input[this.input.activeModel].tools[index].api.headers.push({
        key: "",
        value: "",
      });
    },
    addBody(index) {
      this.input[this.input.activeModel].tools[index].api.body.push({
        key: "",
        value: "",
      });
    },
    addParam(index) {
      this.input[this.input.activeModel].tools[index].schema.push({
        key: "",
        type: "",
      });
    },
    removeHeader(index, i) {
      this.input[this.input.activeModel].tools[index].api.headers.splice(i, 1);
    },
    removeBody(index, i) {
      this.input[this.input.activeModel].tools[index].api.body.splice(i, 1);
    },
    removeParam(index, i) {
      this.input[this.input.activeModel].tools[index].schema.splice(i, 1);
    },
    copyTool(index) {
      this.copy = this.$_.cloneDeep(
        this.input[this.input.activeModel].tools[index]
      );
      this.copiedState[index] = true;
      setTimeout(() => {
        this.$set(this.copiedState, index, false);
      }, 3000);
    },
    pasteTool() {
      this.input[this.input.activeModel].tools.push(this.copy);
    },
    testAPIRequest(key) {
      this.$store
        .dispatch("llm/testAPIRequest", { tool: this.preview[key], key })
        .then(() => {
          this.$set(
            this.previewResponse,
            key,
            this.$store.getters["llm/testAPIResponse"][key]
          );
        })
        .catch(() => {
          this.$set(
            this.previewResponse,
            key,
            this.$store.getters["llm/testAPIResponse"][key]
          );
        });
    },
    async submit() {
      try {
        const payload = {
          data: this.handleOutput(this.input),
          accountID: this.accountID,
          botID: this.input.botID,
        };
        if (
          !payload.botID ||
          (payload.data.activeModel &&
            (!payload.data[this.input.activeModel].APIkey ||
              !payload.data[this.input.activeModel].instructionPrompt))
        ) {
          this.$store.dispatch(
            "setErrorSnackbar",
            "Please fill all required fields"
          );
          return;
        }

        const response = await this.$store.dispatch(
          `llm/${this.action !== "add" ? "update" : "add"}`,
          payload
        );

        this.$emit("success", response);
        this.$store.dispatch("setSnackbar", {
          text: `LLM has been ${
            this.action !== "add" ? "updated" : "added"
          } successfully`,
          color: "success",
          status: true,
        });
        this.$emit("submitted");
      } catch (error) {
        throw error.toString().replace(",", ",\n");
      }
    },
    async resetForm() {
      this.input = {
        botID: "",
        activeModel: "",
      };

      return true;
    },
    emitLoading(status = true) {
      this.$emit("loading", status);
    },
  },
};
</script>

<style lang="css" scoped>
.tools {
  border: 1px solid rgb(13 108 176 / 9%);
  padding: 16px;
  border-radius: 8px;
}

.tool-item {
  border: 2px solid rgb(13 108 176 / 9%);
  padding: 16px;
  border-radius: 8px;
  background: #f0fafd;
  margin-bottom: 16px;
}
.tool-item .tool-item {
  padding: 10px 0 0 0 !important;
}
</style>
